import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '@bbpos/ui';
import { interceptedPath } from './unsaved-changes-link';

/**
 * The confirmLeave of the page currently holding unsaved edits, if any. The
 * layout's sidebar, notification bell and sign-out navigate in code (AppShell
 * onNavigate), which no link listener sees, so they go through guardedLeave.
 */
let activeGuard: ((leave: () => void) => void) | null = null;

/** Run `leave` now, or after the dirty page's Stay / Discard and leave dialog. */
export function guardedLeave(leave: () => void): void {
  if (activeGuard) activeGuard(leave);
  else leave();
}

/**
 * Warn before unsaved edits are thrown away (POS comments item 2: leaving the
 * product page without "Save changes" silently discarded the edits).
 *
 * While `isDirty`:
 * - closing, refreshing or reloading the tab gets the browser's own prompt;
 * - clicking an in-app link (sidebar, page link, breadcrumb) opens a
 *   Stay / Discard and leave dialog;
 * - code that navigates (a button, a shortcut) wraps the move in
 *   `confirmLeave(() => navigate(...))`; navigation from outside the page
 *   (the layout's sidebar) uses `guardedLeave`.
 *
 * Browser Back/Forward cannot be stopped under <BrowserRouter>; that needs a
 * data router (useBlocker).
 *
 * Render the returned `dialog` somewhere in the page.
 */
export function useUnsavedChangesGuard(isDirty: boolean): {
  confirmLeave: (leave: () => void) => void;
  dialog: ReactNode;
} {
  const navigate = useNavigate();
  const [pending, setPending] = useState<(() => void) | null>(null);
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  // Read synchronously so a second request while the dialog is open (Esc
  // pressed inside it also reaches the page's own Esc = back shortcut) is
  // ignored instead of re-opening the dialog the Esc just closed.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    if (!isDirty) return;
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    function handleClick(e: MouseEvent) {
      const path = interceptedPath(e as unknown as Parameters<typeof interceptedPath>[0], window.location);
      if (!path) return;
      // Capture phase on document runs before React's root listener, so this
      // stops a <Link>'s client-side navigation as well as a plain <a>'s.
      e.preventDefault();
      e.stopPropagation();
      setPending(() => () => navigate(path));
    }
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isDirty, navigate]);

  const confirmLeave = useCallback((leave: () => void) => {
    if (!dirtyRef.current) {
      leave();
      return;
    }
    if (pendingRef.current) return;
    setPending(() => leave);
  }, []);

  useEffect(() => {
    if (!isDirty) return;
    activeGuard = confirmLeave;
    return () => {
      if (activeGuard === confirmLeave) activeGuard = null;
    };
  }, [isDirty, confirmLeave]);

  const dialog = pending ? (
    <ConfirmDialog
      open
      title="Discard unsaved changes?"
      description="You have changes that haven't been saved. Leave this page and lose them?"
      confirmLabel="Discard and leave"
      cancelLabel="Stay"
      variant="warning"
      onConfirm={() => {
        const leave = pending;
        setPending(null);
        leave();
      }}
      onCancel={() => setPending(null)}
    />
  ) : null;

  return { confirmLeave, dialog };
}
